import {Button} from '@chekinapp/ui';
import {useEffect, useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {AUTH_INIT_MODELS, RESERVATION_SEARCH_MODE} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {useChekinSDK} from 'context/ChekinSDK';
import PageUniversalLink from 'components/PageUniversalLink';
import {ViewMainInfo} from './components/ViewMainInfo';
import {RadioOptionType} from 'components/common/MultiRadio';
import {OPTION_VALUES} from './utils';
import {usePath} from '../../hooks';
import {useHousing} from './useHousing';
import {SearchOptions} from './components/SearchOptions';
import {Wrapper, ContentButton} from './styled';
import {cn} from '@chekin/guestapp-ui-kit';
import {KIOSKO_BUTTON_CLASSES} from 'utils/kioskoClasses';
import {useLayoutMode} from 'hooks/useLayoutMode';

function SearchReservation() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const [searchMethod, setSearchMethod] = useState<RadioOptionType | undefined>();
  const {modelName, reservationSearchMode} = useChekinSDK();
  const isHousingMode = modelName === AUTH_INIT_MODELS.housing;
  const isUserMode = modelName === AUTH_INIT_MODELS.user;
  const {housing, isLoading: isHousingLoading, housingCountry} = useHousing();
  const layoutMode = useLayoutMode();

  const isLoading = isHousingMode && isHousingLoading;
  const mode =
    (isHousingMode && housing?.reservation_search_mode) ||
    (isUserMode && (reservationSearchMode || RESERVATION_SEARCH_MODE.both)) ||
    undefined;

  const handleNavigationView = useCallback(
    (mode?: RESERVATION_SEARCH_MODE) => {
      const isBookingCode = mode
        ? mode === RESERVATION_SEARCH_MODE.bookingReference
        : searchMethod?.value === OPTION_VALUES.booking_reference;

      const path = isBookingCode
        ? PATHS.searchReservation.bookingCode
        : PATHS.searchReservation.checkingDate;

      return navigate(getFullPath(path), {
        replace: true,
        state: {housingId: housing?.id, housingCountry: housingCountry, mode},
      });
    },
    [navigate, getFullPath, searchMethod, housing, housingCountry],
  );

  const handleBackClick = () => {
    navigate(getFullPath(PATHS.searchReservation.main));
  };

  useEffect(() => {
    if (mode && mode !== RESERVATION_SEARCH_MODE.both) handleNavigationView(mode);
  }, [handleNavigationView, mode]);

  const showContent =
    !isLoading && (mode === RESERVATION_SEARCH_MODE.both || mode === undefined);

  return (
    <PageUniversalLink className="search-reservation-view">
      {showContent && (
        <Wrapper className="kiosko:mt-[360px] kiosko:gap-10">
          <ViewMainInfo
            title={t('find_your_booking')}
            subTitle={t('select_one_of_the_search_methods_to_locate_your_booking')}
          />

          <SearchOptions value={searchMethod} onChange={setSearchMethod} />

          <ContentButton>
            <Button
              onClick={() => handleNavigationView()}
              disabled={!searchMethod && !isLoading}
              className={cn(
                'property-link__confirm-button',
                'min-w-[205px]',
                'kiosko:mx-auto kiosko:mt-4 kiosko:block',
                KIOSKO_BUTTON_CLASSES,
              )}
              variant="default"
            >
              {t('continue_t')}
            </Button>
            {layoutMode === 'kiosko' && (
              <Button
                onClick={handleBackClick}
                disabled={isLoading}
                className={cn(
                  'submit-btn',
                  'min-w-[205px]',
                  'kiosko:mx-auto kiosko:mt-4 kiosko:block',
                  KIOSKO_BUTTON_CLASSES,
                )}
                variant="secondary"
              >
                {t('back')}
              </Button>
            )}
          </ContentButton>
        </Wrapper>
      )}
    </PageUniversalLink>
  );
}

export {SearchReservation};
